// ─── widgets/button.js ────────────────────────────────────────────────────────
// Creates cleverstory.v2.widgets.button objects.

import shortid from 'shortid';
import { cs, getAbsoluteRect, getFontSize, getFontWeight, getColor, getAlignment, safeAlign } from '../utils/dom.js';
import { extractRGBAFromColor, extractBorderRadius, extractBackground, extractShadowProperties } from '../utils/style.js';
import { resolveFontFamily } from '../utils/fonts.js';
import { BUTTON } from 'constants/widgetTypes.jsx';

function extractStylesFromEl(el) {
    return {
        text:      el.textContent.trim(),
        fontFamily: cs(el).fontFamily || 'inherit',
        fontSize:   getFontSize(el),
        fontWeight: getFontWeight(el),
        color:      getColor(el),
        textAlign:  getAlignment(el),
    };
}

/**
 * @param {{ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap }}
 */
export function createButtonWidgetObj({ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap }) {
    const styles = extractStylesFromEl(el);
    const rect   = getAbsoluteRect(el);
    const mRect  = mobileEl
        ? getAbsoluteRect(mobileEl)
        : { width: Math.min(rect.width, 335), height: rect.height, top: rect.top, left: Math.min(rect.left, 20) };

    const mOff = mobileOffsetRect || offsetRect;
    const bx   = Math.round(rect.left  - (offsetRect.left || 0));
    const by   = Math.round(rect.top   - (offsetRect.top  || 0));
    const mbx  = Math.round(mRect.left - (mOff.left || 0));
    const mby  = Math.round(mRect.top  - (mOff.top  || 0));

    const style  = cs(el);
    const bgc    = extractBackground(style);
    const fc     = extractRGBAFromColor(styles.color);
    const fs     = styles.fontSize.toString();
    const mfs    = Math.max(styles.fontSize * 0.9, 12).toString();
    const border = {
        sides:  null,
        color:  extractRGBAFromColor(style.borderColor),
        width:  parseFloat(style.borderWidth || '0').toString(),
        radius: extractBorderRadius(style),
    };

    const { resolvedFamily, fontIds } = resolveFontFamily(styles.fontFamily, fontFamilyMap, fontIdMap);
    const rff    = resolvedFamily || styles.fontFamily;
    const fstyle = {
        bold:      parseFloat(style.fontWeight) >= 600 || style.fontWeight === 'bold',
        italic:    style.fontStyle === 'italic',
        underline: style.textDecoration.includes('underline'),
    };
    const mkFont = (fontSize) => ({
        style: fstyle, fontSize, fontFamily: rff || null,
        lineSpacing: null, letterSpacing: null, color: fc,
    });

    const shadow = extractShadowProperties(style.boxShadow);

    return {
        uid:           `widget_${shortid.generate()}`,
        type:          BUTTON,
        name:          'Button',
        parent:        parentUid,
        order:         orderRef.value++,
        systemDefined: false,
        fonts:         fontIds.length > 0 ? fontIds : null,
        actions:       null,
        parameter:     null,
        properties: {
            desktop: {
                visible: true,
                layout: {
                    transform: { translate: `${bx}px,${by}px`, rotate: '0deg' },
                    width:  Math.round(rect.width)  + 'px',
                    height: Math.round(rect.height) + 'px',
                },
                regular:   { font: mkFont(fs),  background: bgc, border, shadow },
                hover:     { font: mkFont(fs),  background: bgc, border },
                alignment: {
                    position : safeAlign(styles.textAlign),
                    margin: 5
                },
                animation: null,
            },
            mobile: {
                visible: true,
                layout: {
                    transform: { translate: `${mbx}px,${mby}px`, rotate: '0deg' },
                    width:  Math.round(mRect.width)  + 'px',
                    height: Math.round(mRect.height) + 'px',
                },
                regular:   { font: mkFont(mfs), background: bgc, border, shadow },
                hover:     { font: mkFont(mfs), background: bgc, border, shadow: null },
                alignment: {
                    position : safeAlign(styles.textAlign),
                    margin: 5
                },
                animation: null
            },
        },
        content: styles.text || el.value || 'Button',
    };
}